import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { LifeBuoy, Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Accueil", id: "nav-accueil", end: true },
  { to: "/sos", label: "Urgence & SOS", id: "nav-sos" },
  { to: "/compteur", label: "Compteur & outils", id: "nav-compteur" },
  { to: "/guides", label: "Guides de crise", id: "nav-guides" },
  { to: "/passions", label: "Passions & gaming", id: "nav-passions" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm ${
      isActive ? "text-white bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/5"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B1120]/85 border-b border-white/10"
          : "backdrop-blur-md bg-[#0B1120]/50 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-emerald-500 grid place-items-center text-white font-bold shadow-lg shadow-orange-500/20">
            LÉ
          </span>
          <span className="font-[Outfit] font-semibold text-lg tracking-tight hidden sm:inline">
            L'Équipe & Mental
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.id} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/sos"
            data-testid="sos-emergency-button"
            className="hidden sm:inline-flex em-sos-pulse items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-full shadow-lg shadow-orange-500/30"
          >
            <LifeBuoy className="w-4 h-4" />
            <span className="hidden md:inline">Besoin d'aide immédiate</span>
            <span className="md:hidden">SOS</span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-full bg-white/5 border border-white/10"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0B1120]/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`${l.id}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl ${isActive ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/sos"
              onClick={() => setOpen(false)}
              data-testid="sos-emergency-button-mobile"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-3 rounded-full"
            >
              <LifeBuoy className="w-4 h-4" />
              Besoin d'aide immédiate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
